/**
 * Status Utilities
 * 
 * Provides consistent status display logic across the application
 * Implements UI fallback for null/unknown statuses:
 * - Users (patients, physicians, sponsors, staff): default to 'pending'
 * - Clinics/Tenants: default to 'active'
 */

/**
 * Get display status for a user (patient, physician, sponsor, staff)
 * Defaults to 'pending' if status is null, undefined, or 'unknown'
 * 
 * @param {string|null|undefined} status - Raw status value
 * @returns {string} - Display status
 */
export const getUserStatus = (status) => {
  if (!status || status === 'unknown' || status === 'null') {
    return 'pending';
  }
  return status;
};

/**
 * Get display status for a clinic/tenant
 * Defaults to 'active' if status is null, undefined, or 'unknown'
 * 
 * @param {string|null|undefined} status - Raw status value
 * @returns {string} - Display status
 */
export const getClinicStatus = (status) => {
  if (!status || status === 'unknown' || status === 'null') {
    return 'active';
  }
  return status;
};

/**
 * Get status color for Material-UI Chip component
 * 
 * @param {string} status - Status value
 * @returns {string} - MUI color name
 */
export const getStatusColor = (status) => {
  const colorMap = {
    active: 'success',
    inactive: 'default',
    pending: 'warning',
    suspended: 'error',
    invited: 'info',
  };
  return colorMap[status] || 'default';
};

/**
 * Get formatted status label (capitalized)
 * 
 * @param {string} status - Status value
 * @returns {string} - Formatted label
 */
export const getStatusLabel = (status) => {
  if (!status) return 'Pending';
  return status.charAt(0).toUpperCase() + status.slice(1);
};
